/**
 * The light / dark switch. Colours come from the surface and `DFC_PALETTE` only: the night thumb
 * and orb are DFC green with a black crescent (14.1:1), the day thumb is white with an ink sun.
 * Every movement is a transform or opacity change and stops under `prefers-reduced-motion`.
 */
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { ButtonBase } from '@mui/material';
import { DFC_PALETTE, withAlpha } from '../../../../../Utils/dfcBrand';
import { DfcSurface } from '../../../../../Utils/dfcSurface';

const omit = (...props: string[]) => ({
  shouldForwardProp: (prop: PropertyKey) => !props.includes(prop as string),
});

type Props = { surface: DfcSurface; dark: boolean };

const EASE = 'cubic-bezier(0.34, 1.4, 0.64, 1)';

const focusRing = (surface: DfcSurface) => `
  &:focus-visible {
    outline: 2px solid ${surface.dark ? DFC_PALETTE.green : DFC_PALETTE.black};
    outline-offset: 2px;
  }
`;

/** Stretches the hit area to 44px without changing the drawn size. */
const hitArea = `
  &::after {
    content: '';
    position: absolute;
    inset: -6px;
  }
`;

const reducedMotion = `
  @media (prefers-reduced-motion: reduce) {
    &,
    & * {
      transition: none !important;
      animation: none !important;
    }
  }
`;

/* ---- Desktop: the day / night pill ---- */

export const SwitchPill = styled(ButtonBase, omit('surface', 'dark'))<Props>`
  && {
    position: relative;
    flex: 0 0 auto;
    width: 60px;
    height: 32px;
    border-radius: 999px;
    overflow: visible;
    background: ${({ surface, dark }) => (dark ? DFC_PALETTE.black : surface.track)};
    border: 1px solid ${({ surface }) => surface.border};
    box-shadow: inset 0 1px 3px ${({ surface }) => withAlpha(surface.scrim, 0.12)};
    transition:
      background 0.35s ease,
      border-color 0.35s ease;
    ${hitArea}
    ${({ surface }) => focusRing(surface)}
    ${reducedMotion}
  }
`;

export const Thumb = styled('span', omit('surface', 'dark'))<Props>`
  position: absolute;
  top: 3px;
  left: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: ${({ dark }) => (dark ? DFC_PALETTE.green : DFC_PALETTE.white)};
  color: ${({ dark, surface }) => (dark ? DFC_PALETTE.black : surface.ink)};
  box-shadow: 0 2px 6px ${({ surface }) => withAlpha(surface.scrim, 0.25)};
  /* Right-to-left needs no override: the RTL stylis plugin turns left: 3px into right: 3px
     and negates this translateX, so the thumb rolls leftwards. */
  transform: translateX(${({ dark }) => (dark ? '28px' : '0')});
  transition:
    transform 0.45s ${EASE},
    background 0.35s ease;
`;

/* ---- Mobile: the orb ---- */

export const Orb = styled(ButtonBase, omit('surface', 'dark'))<Props>`
  && {
    position: relative;
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    overflow: visible;
    background: ${({ surface, dark }) => (dark ? DFC_PALETTE.green : surface.track)};
    color: ${({ surface, dark }) => (dark ? DFC_PALETTE.black : surface.ink)};
    transition: background 0.35s ease;
    ${hitArea}
    ${({ surface }) => focusRing(surface)}
    ${reducedMotion}
  }
`;

/* ---- Shared: the sun and moon glyphs, the stars ---- */

/** Sun and moon sit on top of each other; the outgoing one spins away as the other spins in. */
export const Glyph = styled('span', omit('visible'))<{ visible: boolean }>`
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: ${({ visible }) => (visible ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.4)')};
  transition:
    opacity 0.3s ease,
    transform 0.45s ${EASE};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.15); }
`;

/** Where each star sits: three across the empty side of the pill, one tucked on the orb. */
const STAR_SPOTS = {
  a: { top: '8px', left: '10px', size: 3, delay: '0s' },
  b: { top: '17px', left: '18px', size: 2, delay: '0.6s' },
  c: { top: '10px', left: '25px', size: 2, delay: '1.1s' },
  orb: { top: '3px', left: '26px', size: 4, delay: '0.3s' },
} as const;

export const Star = styled('span', omit('dark', 'at'))<{
  dark: boolean;
  at: keyof typeof STAR_SPOTS;
}>`
  position: absolute;
  top: ${({ at }) => STAR_SPOTS[at].top};
  left: ${({ at }) => STAR_SPOTS[at].left};
  width: ${({ at }) => STAR_SPOTS[at].size}px;
  height: ${({ at }) => STAR_SPOTS[at].size}px;
  border-radius: 999px;
  background: ${({ at }) => (at === 'orb' ? DFC_PALETTE.black : DFC_PALETTE.white)};
  opacity: 0;
  transform: scale(0);
  transition:
    opacity 0.3s ease,
    transform 0.4s ${EASE};
  pointer-events: none;

  ${({ dark, at }) =>
    dark
      ? `
    opacity: 1;
    transform: scale(1);
    transition-delay: 0.15s;
    animation: ${twinkle} 2.4s ease-in-out ${STAR_SPOTS[at].delay} infinite;
  `
      : ''}
`;
